import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

import { useAudit } from "../contexts/auditContext";
import { type AuditSession } from "../models/audit/auditSession";
import { Citation } from "../models/audit/citation";
import { citationToBoundingBox } from "../pages/audit/auditWorkPaperViewV2/components/attributeTestAnalysis/types";
import { type ViewerBoundingBox } from "../pages/extraction/viewExtraction/FileViewer";
import { useCellCitationDetail } from "./audit/useCellCitationDetail";
import { useCitationNavigation } from "./useCitationNavigation";

interface UseSupportCellHighlightProps {
  auditSession: AuditSession;
  procedureSessionId: string;
  sourceRowId?: string;
  supportColumnId?: string;
}

/**
 * Headless orchestration for the support-cell evidence experience — the
 * support-cell analogue of `useAttributeTestHighlight`. Lazy-fetches the
 * support cell's citations (`useCellCitationDetail`), owns the citation
 * navigation, and exposes the `updateDocumentForCitation` driver that lights up
 * the shared document viewer (which reads the active document/URL from
 * `useAudit()`). On first load the navigation auto-selects the first citation,
 * so the viewer opens on the support cell's source evidence.
 */
export function useSupportCellHighlight({
  auditSession,
  procedureSessionId,
  sourceRowId,
  supportColumnId,
}: UseSupportCellHighlightProps) {
  const { selectedDocument, updateCurrentDocument } = useAudit();
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  const { data } = useCellCitationDetail(
    procedureSessionId,
    sourceRowId,
    supportColumnId,
  );
  const citations = useMemo(() => data?.citations ?? [], [data]);

  const onCitationSelectRef = useRef<(citation: Citation) => void>(() => {});
  const navigation = useCitationNavigation({
    filteredCitations: citations,
    onCitationSelect: (citation) => onCitationSelectRef.current(citation),
  });

  const updateDocumentForCitation = useCallback(
    (citation: Citation) => {
      const sourceDocument = auditSession.documents.find(
        (doc) => doc.documentId === citation.documentId,
      );
      if (!sourceDocument) return;

      navigation.setCurrentSelectionValue(citation.shortSummary || undefined);
      navigation.setCurrentBoundingBox(citationToBoundingBox(citation));

      updateCurrentDocument({
        id: sourceDocument.id,
        documentId: sourceDocument.documentId,
        fileName: sourceDocument.name || undefined,
        fileType: sourceDocument.fetchableDocument?.fileType,
        fetchableDocument: sourceDocument.fetchableDocument || undefined,
        isMissing: false,
        type: sourceDocument.fetchableDocument?.fileType,
        procedureSessionId: auditSession.id,
      });
      setShowDocumentViewer(true);
    },
    [
      auditSession.id,
      auditSession.documents,
      updateCurrentDocument,
      navigation,
    ],
  );

  // Keep the selection ref pointed at the latest callback BEFORE passive effects
  // run: when the fold-out remounts onto a warm citation cache (e.g. rapid
  // navigation back to a visited cell), the navigation hook's auto-select fires
  // in a mount-time passive effect and would otherwise hit the initial no-op,
  // leaving the viewer stuck on its empty state.
  useLayoutEffect(() => {
    onCitationSelectRef.current = updateDocumentForCitation;
  }, [updateDocumentForCitation]);

  const handleCitationClick = useCallback(
    (citation: Citation) => {
      const idx = citations.findIndex((c) => c.id === citation.id);
      if (idx >= 0) {
        navigation.setActiveEvidenceIndex(idx);
      }
      updateDocumentForCitation(citation);
    },
    [citations, navigation, updateDocumentForCitation],
  );

  // Highlights for the viewed document — every citation of this cell on the
  // currently-shown document (mirrors `useCitationActions`' derivation).
  const currentDocId =
    selectedDocument?.documentId ??
    citations[navigation.activeEvidenceIndex]?.documentId;

  const viewerBoundingBoxes: ViewerBoundingBox[] | undefined = useMemo(() => {
    if (!currentDocId) return undefined;
    const boxes = citations
      .filter((c) => c.documentId === currentDocId && !!c.boundingBox)
      .map((c) => ({
        id: c.id || undefined,
        boundingBox: c.boundingBox!,
        pageNumber: c.pageNumber || undefined,
        pageWidth: c.pageWidth || undefined,
        pageHeight: c.pageHeight || undefined,
      }));
    return boxes.length > 0 ? boxes : undefined;
  }, [citations, currentDocId]);

  const selectedBoundingBoxId = navigation.currentBoundingBox?.citationId;
  const selectedBoundingBoxIndex = useMemo(() => {
    if (!viewerBoundingBoxes || !selectedBoundingBoxId) return undefined;
    const idx = viewerBoundingBoxes.findIndex(
      (b) => b.id === selectedBoundingBoxId,
    );
    return idx >= 0 ? idx : undefined;
  }, [viewerBoundingBoxes, selectedBoundingBoxId]);

  return {
    citations,
    navigation,
    showDocumentViewer,
    handleCitationClick,
    viewerBoundingBoxes,
    selectedBoundingBoxId,
    selectedBoundingBoxIndex,
  };
}
